import React, { useState, useEffect, useRef, useCallback } from 'react';
import { CapturedImage, AnalysisResult, CaptureSequence } from './types';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { MoveHorizontal, Columns2 } from 'lucide-react';
import DifferenceMetrics from './DifferenceMetrics';
import AnalysisPanels from './AnalysisPanels';
import { FullScreenImageDialog } from '@/components/ui/full-screen-image-dialog';
import { formatDateLocale } from '@/lib/utils';

interface PostureAnalysis {
    id: string;
    createdAt: any;
    captured: Record<CaptureSequence, CapturedImage | null>;
    // ... other fields
}

interface ComparisonViewProps {
    analysisA: PostureAnalysis; // Before
    analysisB: PostureAnalysis; // After
    /** When false, Posterior tab is hidden. */
    experimentalFeaturesEnabled?: boolean;
}

type ComparisonMode = 'side-by-side' | 'slider';

/** Clamp a slider percentage (0–100), tolerant of NaN/out-of-range input. */
function clampSliderPct(value: number): number {
    if (!Number.isFinite(value)) return 50;
    return Math.min(100, Math.max(0, value));
}

/**
 * The date label of a compared analysis. `createdAt` can be a Date, a Firestore Timestamp
 * or the API's `{ _seconds, _nanoseconds }`; `formatDateLocale` reads all of them (wrapping
 * the last in `new Date(...)` gave an Invalid Date and the label read "N/A").
 */
export function formatAnalysisDate(analysis: Pick<PostureAnalysis, 'createdAt'>): string {
    return formatDateLocale(analysis.createdAt, {
        year: 'numeric', month: 'numeric', day: 'numeric'
    });
}

const ComparisonView: React.FC<ComparisonViewProps> = ({
    analysisA,
    analysisB,
    experimentalFeaturesEnabled = false,
}) => {
    const [view, setView] = useState<CaptureSequence>('Anterior');
    const [mode, setMode] = useState<ComparisonMode>('side-by-side');
    const [sliderPct, setSliderPct] = useState(50);
    const containerRef = useRef<HTMLDivElement>(null);
    const draggingRef = useRef(false);

    // When experimental turns off and view is Posterior, switch to Anterior
    useEffect(() => {
        if (!experimentalFeaturesEnabled && view === 'Posterior') {
            setView('Anterior');
        }
    }, [experimentalFeaturesEnabled, view]);

    const before = analysisA;
    const after = analysisB;

    const imgBefore = before.captured[view];
    const imgAfter = after.captured[view];

    const updateFromClientX = useCallback((clientX: number) => {
        const el = containerRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        if (rect.width === 0) return;
        setSliderPct(clampSliderPct(((clientX - rect.left) / rect.width) * 100));
    }, []);

    const handlePointerDown = useCallback((e: React.PointerEvent) => {
        draggingRef.current = true;
        (e.target as Element).setPointerCapture?.(e.pointerId);
        updateFromClientX(e.clientX);
    }, [updateFromClientX]);

    const handlePointerMove = useCallback((e: React.PointerEvent) => {
        if (!draggingRef.current) return;
        updateFromClientX(e.clientX);
    }, [updateFromClientX]);

    const handlePointerUp = useCallback(() => {
        draggingRef.current = false;
    }, []);

    const handleHandleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'ArrowLeft') {
            e.preventDefault();
            setSliderPct((p) => clampSliderPct(p - 5));
        } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            setSliderPct((p) => clampSliderPct(p + 5));
        } else if (e.key === 'Home') {
            e.preventDefault();
            setSliderPct(0);
        } else if (e.key === 'End') {
            e.preventDefault();
            setSliderPct(100);
        }
    }, []);

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <Tabs value={view} onValueChange={(v) => setView(v as CaptureSequence)}>
                    <TabsList>
                        <TabsTrigger value="Anterior">Anterior</TabsTrigger>
                        {experimentalFeaturesEnabled && <TabsTrigger value="Posterior">Posterior</TabsTrigger>}
                        <TabsTrigger value="Left">Left</TabsTrigger>
                        <TabsTrigger value="Right">Right</TabsTrigger>
                    </TabsList>
                </Tabs>

                <div className="flex items-center gap-2">
                    <div className="flex rounded-md border border-line p-0.5">
                        <Button
                            type="button"
                            size="sm"
                            variant={mode === 'side-by-side' ? 'secondary' : 'ghost'}
                            className="h-7 px-2"
                            onClick={() => setMode('side-by-side')}
                            aria-pressed={mode === 'side-by-side'}
                        >
                            <Columns2 className="h-3.5 w-3.5" />
                            <span className="ml-1 hidden sm:inline">Side by side</span>
                        </Button>
                        <Button
                            type="button"
                            size="sm"
                            variant={mode === 'slider' ? 'secondary' : 'ghost'}
                            className="h-7 px-2"
                            onClick={() => setMode('slider')}
                            aria-pressed={mode === 'slider'}
                        >
                            <MoveHorizontal className="h-3.5 w-3.5" />
                            <span className="ml-1 hidden sm:inline">Slider</span>
                        </Button>
                    </div>
                </div>
            </div>

            {mode === 'side-by-side' ? (
                <div className="grid grid-cols-2 gap-4">
                    {/* Before */}
                    <Card>
                        <CardContent className="p-2">
                            <div className="text-center text-xs font-medium mb-2 text-muted-foreground">
                                {formatAnalysisDate(before)}
                            </div>
                            <div className="aspect-[3/4] bg-black rounded-md overflow-hidden relative">
                                {imgBefore ? (
                                    <FullScreenImageDialog imageUrl={imgBefore.imageData} altText="Before image">
                                        <img src={imgBefore.imageData} className="w-full h-full object-contain cursor-pointer" alt="Before" />
                                    </FullScreenImageDialog>
                                ) : (
                                    <div className="flex items-center justify-center h-full text-muted-foreground text-xs">No Image</div>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* After */}
                    <Card>
                        <CardContent className="p-2">
                            <div className="text-center text-xs font-medium mb-2 text-cyan-500">
                                {formatAnalysisDate(after)}
                            </div>
                            <div className="aspect-[3/4] bg-black rounded-md overflow-hidden relative">
                                {imgAfter ? (
                                    <FullScreenImageDialog imageUrl={imgAfter.imageData} altText="After image">
                                        <img src={imgAfter.imageData} className="w-full h-full object-contain cursor-pointer" alt="After" />
                                    </FullScreenImageDialog>
                                ) : (
                                    <div className="flex items-center justify-center h-full text-muted-foreground text-xs">No Image</div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            ) : (
                <Card>
                    <CardContent className="p-2">
                        <div
                            ref={containerRef}
                            className="relative aspect-[3/4] w-full max-w-md mx-auto touch-none select-none overflow-hidden rounded-md bg-black"
                            onPointerDown={handlePointerDown}
                            onPointerMove={handlePointerMove}
                            onPointerUp={handlePointerUp}
                            onPointerCancel={handlePointerUp}
                        >
                            {/* Bottom layer: after (fully visible). */}
                            {imgAfter ? (
                                <img
                                    src={imgAfter.imageData}
                                    className="pointer-events-none absolute inset-0 h-full w-full object-contain"
                                    alt="After"
                                />
                            ) : (
                                <div className="flex h-full items-center justify-center text-xs text-muted-foreground">No Image</div>
                            )}

                            {/* Top layer: before, clipped to the slider position. */}
                            {imgBefore && (
                                <div
                                    className="pointer-events-none absolute inset-0 overflow-hidden"
                                    style={{ clipPath: `inset(0 ${100 - sliderPct}% 0 0)` }}
                                >
                                    <img
                                        src={imgBefore.imageData}
                                        className="absolute inset-0 h-full w-full object-contain"
                                        alt="Before"
                                    />
                                </div>
                            )}

                            {/* Divider line */}
                            <div
                                className="pointer-events-none absolute inset-y-0 w-0.5 bg-white/80"
                                style={{ left: `${sliderPct}%` }}
                            />

                            {/* Drag handle — pointer + keyboard (arrow keys / Home / End) accessible. */}
                            <button
                                type="button"
                                role="slider"
                                aria-label="Before/after comparison position"
                                aria-valuemin={0}
                                aria-valuemax={100}
                                aria-valuenow={Math.round(sliderPct)}
                                tabIndex={0}
                                className="absolute top-1/2 flex h-8 w-8 -translate-x-1/2 -translate-y-1/2 cursor-ew-resize touch-none items-center justify-center rounded-full border-2 border-white bg-primary text-primary-foreground shadow-lg"
                                style={{ left: `${sliderPct}%` }}
                                onPointerDown={handlePointerDown}
                                onKeyDown={handleHandleKeyDown}
                            >
                                <MoveHorizontal className="h-4 w-4" />
                            </button>

                            {/* Date labels */}
                            <div className="pointer-events-none absolute left-2 top-2 rounded bg-black/60 px-1.5 py-0.5 text-[10px] text-white">
                                {formatAnalysisDate(before)}
                            </div>
                            <div className="pointer-events-none absolute right-2 top-2 rounded bg-black/60 px-1.5 py-0.5 text-[10px] text-white">
                                {formatAnalysisDate(after)}
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Metrics Diff would go here if we computed them */}
            {/* <DifferenceMetrics ... /> */}
        </div>
    );
};

export default ComparisonView;
